import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { formatAddress } from '@/models/address';
import { Entity, useEntities } from '@/models/entity';

import {
  AfterSalesTraitIcon,
  StockLocationTraitIcon,
  StoreTraitIcon,
  WorkshopTraitIcon,
} from '../../../../../Entities/subsections/Entities/components/TraitIcon';

const ENTITIES_PER_PAGE = 20;

export const EntitiesSection = ({ companyId }: { companyId: string }) => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);

  const {
    data: { entities = [], meta } = { entities: [] },
    isLoading,
    isPlaceholderData,
    isError,
  } = useEntities(
    {
      companies: [companyId],
      limit: ENTITIES_PER_PAGE,
      offset: (page - 1) * ENTITIES_PER_PAGE,
    },
    {
      keepPreviousData: true,
    }
  );

  return (
    <TableQueryWrapper isLoading={isLoading || isPlaceholderData} isError={isError}>
      <Table
        columnWidths={['auto', '1fr', '1fr']}
        bordered
        ariaLabel={t({
          id: 'settings.organization.invoicing.companies.detail.entities.table.aria_label',
          message: 'Entities linked to this company',
        })}
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.organization.invoicing.companies.detail.entities.table.header.trait">
                Trait
              </Trans>
            </Column>
            <Column>
              <Trans id="settings.organization.invoicing.companies.detail.entities.table.header.name">
                Name
              </Trans>
            </Column>
            <Column>
              <Trans id="settings.organization.invoicing.companies.detail.entities.table.header.address">
                Address
              </Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {entities.map((entity) => (
            <EntityRow key={entity.id} entity={entity} />
          ))}
          {entities.length === 0 && (
            <Row>
              <Cell isWholeRow>
                <Paragraph color="secondary">
                  <Trans id="settings.organization.invoicing.companies.detail.entities.no-results">
                    This company has no entities yet.
                  </Trans>
                </Paragraph>
              </Cell>
            </Row>
          )}
        </Body>
        {entities.length > 0 && (
          <PaginationFooter
            itemsPerPage={ENTITIES_PER_PAGE}
            page={page}
            onPageChange={setPage}
            count={meta?.count}
          />
        )}
      </Table>
    </TableQueryWrapper>
  );
};

const EntityRow = ({ entity }: { entity: Entity }) => {
  const isStore = entity.entityOrganizations.some(
    (entityOrganization) => entityOrganization.trait === 'store'
  );
  const isAfterSales = entity.entityOrganizations.some(
    (entityOrganization) => entityOrganization.trait === 'aftersales'
  );
  const isWorkshop = entity.entityOrganizations.some(
    (entityOrganization) => entityOrganization.trait === 'workshop'
  );
  const isStockLocation = entity.entityOrganizations.some(
    (entityOrganization) => entityOrganization.trait === 'stockLocation'
  );

  return (
    <Row to={`/settings_beta/network/entities/${entity.id}`}>
      <Cell>
        <Stack row gap="0.25rem" style={{ fontSize: '1rem' }} className="text-secondary">
          {isStore && <StoreTraitIcon />}
          {isAfterSales && <AfterSalesTraitIcon />}
          {isWorkshop && <WorkshopTraitIcon />}
          {isStockLocation && <StockLocationTraitIcon />}
        </Stack>
      </Cell>
      <Cell isLink>
        <Paragraph weight="medium">{entity.name}</Paragraph>
      </Cell>
      <Cell>
        <Paragraph size="200">{entity.address ? formatAddress(entity.address) : '-'}</Paragraph>
      </Cell>
    </Row>
  );
};
